import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { ErrorHandler, inject } from '@angular/core';
import { ApiService } from 'app/api.service';
import { AuthService } from 'app/core/auth/auth.service';
import { SILENT_ERRORS } from 'app/core/auth/http-context';
import { ApiError } from 'app/shared/api-error';
import { APP_VERSION } from 'environments/version';
import { getLocal, LOCAL_SESSION_KEY, setLocal } from 'global';
import { catchError, Observable, tap, throwError } from 'rxjs';

const alreadyLoggedFlag = Symbol('alreadyLogged');

function extractServerMessage(body: any): string | null {
    if (!body) return null;
    if (typeof body === 'string') return body;
    return body.msg || body.message || body.error || null;
}
/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const errorHandler = inject(ErrorHandler);
    const _api = inject(ApiService);
    const startedAt = Date.now();
    // Peticiones marcadas como silenciosas (ver SILENT_ERRORS): fallan sin alert global ni captura
    // global de errores. El error se sigue propagando al llamador, que lo maneja por su cuenta.
    const silent = req.context.get(SILENT_ERRORS);
    // Clone the request object
    let newReq = req.clone();

    if (newReq.url.includes('amazonaws.com')) {
        return next(newReq);
    }

    // Retrieve session and version
    const session = getLocal(LOCAL_SESSION_KEY);
    const version = APP_VERSION;

    if (authService.accessToken) {
        if (req.method.toUpperCase() === 'GET') {
            let params = req.params;

            if (version) params = params.append('version', version);
            if (session) params = params.append('session', session);

            newReq = req.clone({
                headers: req.headers.set('Authorization', 'Bearer ' + authService.accessToken),
                params: params,
            });
        } else if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(req.method.toUpperCase())) {
            // A diferencia de un objeto cualquiera JSON, el formData requiere un append para agregar propiedades. Por eso se hace esta verificación
            const headers = req.headers.set('Authorization', 'Bearer ' + authService.accessToken);
            if (req.body instanceof FormData) {
                // Agregar al FormData
                req.body.append('version', version);
                req.body.append('session', session);

                newReq = req.clone({ headers, body: req.body });
            } else {
                // Manejar otros tipos de cuerpos (ej. JSON)
                const body = typeof req.body === 'object' && req.body !== null ? { ...req.body } : {};
                body['version'] = version;
                body['session'] = session;

                newReq = req.clone({ headers, body });
            }
        }
    }

    // Response handling
    return next(newReq).pipe(
        tap((event) => {
            if (event instanceof HttpResponse) {
                const session = event.headers.get('session') || event.body?.['session'];
                if (session) {
                    setLocal(LOCAL_SESSION_KEY, session);
                }
                const body = event.body;
                if (body && typeof body === 'object' && 'success' in body && body.success === false) {
                    const msg = extractServerMessage(body) || 'Operación fallida';
                    const apiErr = new ApiError(msg, body, event.status, {
                        method: req.method,
                        url: req.urlWithParams,
                        durationMs: Date.now() - startedAt,
                        okButFailed: true,
                    });

                    // Peticiones silenciosas: no capturar globalmente ni mostrar Swal; solo propagar.
                    if (!silent) {
                        if (!(apiErr as any)[alreadyLoggedFlag]) {
                            (apiErr as any)[alreadyLoggedFlag] = true;
                            errorHandler.handleError(apiErr);
                        }

                        // Hotfix: si el backend envía `options.suggestions`, NO mostrar el Swal
                        // genérico (queda tapado por mat-dialog). El caller (modal) maneja el Swal
                        // propio con z-index forzado + dropdown clickeable.
                        const suggestions = (body as any)?.options?.suggestions;
                        const hasSuggestions = Array.isArray(suggestions) && suggestions.length > 0;
                        if (!hasSuggestions) {
                            _api.setError(event.body);
                        }
                    }
                    throw apiErr;
                }
            }
            return event;
        }),
        catchError((err) => {
            // Catch "401 Unauthorized" responses
            if (err instanceof HttpErrorResponse && err?.status === 401) {
                // Si hay impersonation activa, salimos de impersonation (restaura al
                // super_admin) en vez de cerrar la sesión por completo; en caso normal
                // hace sign-out + reload.
                authService.handleAuthFailure();
            }

            let apiErr: ApiError;
            console.log('🔴 THROWING ERROR FROM HERE');

            if (err instanceof HttpErrorResponse) {
                const serverMsg = extractServerMessage(err.error);
                const msg = serverMsg || `HTTP ${err.status} en ${req.method} ${req.url}`;
                apiErr = new ApiError(msg, err.error, err.status, {
                    method: req.method,
                    url: req.urlWithParams,
                    durationMs: Date.now() - startedAt,
                    statusText: err.statusText,
                    type: err.type,
                });
            } else if (err instanceof ApiError) {
                apiErr = err;
            } else {
                apiErr = new ApiError('Error inesperado en petición', undefined, undefined, {
                    method: req.method,
                    url: req.urlWithParams,
                    durationMs: Date.now() - startedAt,
                    original: String(err),
                });
            }

            // Peticiones silenciosas: no capturar globalmente; solo propagar al llamador.
            // (El manejo de 401/refresh de arriba se conserva intacto.)
            if (!silent && !(apiErr as any)[alreadyLoggedFlag]) {
                (apiErr as any)[alreadyLoggedFlag] = true;
                errorHandler.handleError(apiErr);
            }
            return throwError(() => apiErr);
        })
    );
};
